import {useApolloClient, ApolloClient} from "@apollo/client";
import {Dialog, List, ListItemButton, TextField, Theme} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {FormattedButton, CloseIcon, DialogActions, DialogContent, FormattedDialogTitle} from "@dvrt/graphql-widgets";
import {createElement, useEffect, useState} from "react";
import {ExtensionListDocument} from "../queries.generated";
import {ExtensionListQuery, ExtensionListQueryVariables} from "../queries.generated";
import {FormattedMessage, MessageDescriptor, defineMessage} from "react-intl";
import {Capability} from "@/api";
import {NewMemberInfo} from "./types";
import {formatEndpoint} from "@/components/TargetSelector";
import {msg_close} from "@/utils/translations";

const groupTitle: MessageDescriptor = {
    id: "call_group_memberships.add.group",
    defaultMessage: "Add user to this group ({memberCount}/{size})",
};

const queueTitle: MessageDescriptor = {
    id: "call_group_memberships.add.queue",
    defaultMessage: "Add user to this call queue",
};

type ContentProps = {
    submit: (members: NewMemberInfo[]) => void;
    domainId: number;
    excludedNumberIds: number[];
    isCallQueue: boolean;
    close: () => void;
    groupSize: number;
    formMemberCount: number;
};

type AddNewMemberWidgetProps = {
    open: boolean;
} & ContentProps;

const callQueueCaps = [Capability.AttendantConsole, Capability.CcAgent, Capability.CcSupervisor];

type User = ExtensionListQuery["users"]["items"][0];

const callQueueFilterFactory = (caps: Record<number, Capability[]>, isCallQueue: boolean) => (op: User) => {
    if (!isCallQueue) return true;

    const extCap = caps[op.id];

    if (!extCap) return false;

    for (const cap of callQueueCaps) {
        if (extCap.includes(cap)) return true;
    }

    return false;
};

async function fetch(client: ApolloClient<object>, domainId: number, isCallQueue: boolean): Promise<NewMemberInfo[]> {
    const res = await client.query<ExtensionListQuery, ExtensionListQueryVariables>({
        query: ExtensionListDocument,
        fetchPolicy: "no-cache",
        variables: {
            domainId,
        },
    });

    const extCaps = res.data.usersCapabilities.reduce((previousValue: Record<number, Capability[]>, currentValue) => {
        previousValue[currentValue.id] = currentValue.capabilities;
        return previousValue;
    }, {});

    const ops = res.data.users.items
        .filter(callQueueFilterFactory(extCaps, isCallQueue))
        .map((e) => ({numberId: e.numberId, shortNumber: e.number.shortNumber, displayName: e.displayName}));

    ops.sort((a, b) => a.shortNumber.localeCompare(b.shortNumber));

    return ops;
}

const useNumberOptions = (domainId: number, isCallQueue: boolean): NewMemberInfo[] => {
    const client = useApolloClient();
    const [options, setOptions] = useState<NewMemberInfo[]>([]);

    useEffect(() => {
        fetch(client, domainId, isCallQueue).then((ops: NewMemberInfo[]) => {
            setOptions(ops);
        });
    }, [client, domainId, isCallQueue]);

    return options;
};

const useStyles = makeStyles()((theme: Theme) => ({
    preContent: {
        padding: "1.5em",
        paddingBottom: 0,
    },
    selectedFeedback: {
        marginTop: "0.2em",
        display: "flex",
        maxWidth: 300,
        flexWrap: "wrap",
    },
    selectedFeedbackItem: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
        borderRadius: theme.shape.borderRadius,
        padding: "3px 6px",
        marginRight: 5,
        marginBottom: 5,
        display: "flex",
    },
}));

const optionsFilterFactory = (excludedNumberIds: number[], filter: string) => (option: NewMemberInfo) => {
    if (excludedNumberIds.includes(option.numberId)) return false;

    if (filter.length) {
        if (
            option.shortNumber.toLowerCase().search(filter.toLowerCase()) === -1 &&
            option.displayName.toLowerCase().search(filter.toLowerCase()) === -1
        )
            return false;
    }

    return true;
};

const FormContent = ({
    domainId,
    submit,
    excludedNumberIds,
    isCallQueue,
    close,
    formMemberCount,
    groupSize,
}: ContentProps) => {
    const {classes} = useStyles();

    const [filter, setFilter] = useState<string>("");
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const unFilteredOptions = useNumberOptions(domainId, isCallQueue);
    const options = unFilteredOptions.filter(optionsFilterFactory(excludedNumberIds, filter));
    const selectedOptions = unFilteredOptions
        .filter((o) => selectedIds.includes(o.numberId))
        .reduce((previousValue: Record<number, NewMemberInfo>, currentValue) => {
            previousValue[currentValue.numberId] = currentValue;
            return previousValue;
        }, {});

    const deselectItem = (numberId: number) => {
        const newItems = structuredClone(selectedIds);
        newItems.splice(newItems.indexOf(numberId), 1);
        setSelectedIds(newItems);
    };

    return (
        <>
            <FormattedDialogTitle
                title={isCallQueue ? queueTitle : groupTitle}
                values={{memberCount: formMemberCount + selectedIds.length, size: groupSize}}
            />
            <div className={classes.preContent}>
                <TextField
                    value={filter}
                    onChange={(ev) => setFilter(ev.target.value)}
                    variant="outlined"
                    label={<FormattedMessage id="filter" defaultMessage="Filter" />}
                    size="small"
                    fullWidth
                    InputProps={{
                        endAdornment: filter.length ? (
                            <CloseIcon style={{cursor: "pointer"}} onClick={() => setFilter("")} />
                        ) : null,
                    }}
                />
                {selectedIds.length && (
                    <div className={classes.selectedFeedback}>
                        {selectedIds.map((numberId) => (
                            <div key={numberId} className={classes.selectedFeedbackItem}>
                                <div style={{marginRight: 4}}>
                                    {selectedOptions[numberId].shortNumber} - {selectedOptions[numberId].displayName}
                                </div>
                                <CloseIcon
                                    onClick={() => deselectItem(numberId)}
                                    style={{
                                        cursor: "pointer",
                                        marginBottom: -2,
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <DialogContent>
                <List>
                    {options.map((opt) => (
                        <ListItemButton
                            key={opt.numberId}
                            onClick={() => {
                                if (selectedIds.includes(opt.numberId)) deselectItem(opt.numberId);
                                else if (!isCallQueue && formMemberCount + selectedIds.length >= groupSize) return;
                                else setSelectedIds([...selectedIds, opt.numberId]);
                            }}
                            selected={selectedIds.includes(opt.numberId)}
                        >
                            {formatEndpoint(opt)}
                        </ListItemButton>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <FormattedButton
                    disabled={!selectedIds.length}
                    variant="text"
                    message={defineMessage({id: "submit", defaultMessage: "Submit"})}
                    onClick={() => {
                        submit(Object.values(selectedOptions));
                        close();
                    }}
                />
                <FormattedButton
                    variant="text"
                    message={defineMessage({id: "cancel", defaultMessage: "Cancel"})}
                    onClick={close}
                    color="secondary"
                />
            </DialogActions>
        </>
    );
};

const FullContent = ({close, size}: {size: number; close: () => void}) => {
    return (
        <>
            <FormattedDialogTitle title={groupTitle} values={{memberCount: size, size: size}} />
            <DialogContent>
                <FormattedMessage id="call_group_memberships.group_is_full" defaultMessage="Group is full" />
            </DialogContent>
            <DialogActions>
                <FormattedButton variant="text" message={msg_close} onClick={close} />
            </DialogActions>
        </>
    );
};

const Content = (props: ContentProps) => {
    if (props.groupSize == props.formMemberCount) return <FullContent close={props.close} size={props.groupSize} />;
    return createElement(FormContent, props);
};

export const AddNewMemberDialog = ({open, ...contentProps}: AddNewMemberWidgetProps) => {
    return (
        <Dialog open={open} onClose={contentProps.close}>
            <Content
                isCallQueue={contentProps.isCallQueue}
                submit={contentProps.submit}
                domainId={contentProps.domainId}
                excludedNumberIds={contentProps.excludedNumberIds}
                close={contentProps.close}
                groupSize={contentProps.groupSize}
                formMemberCount={contentProps.formMemberCount}
            />
        </Dialog>
    );
};
